import type { Project } from '@/features/projects/api/projects';

export type Permissions = NonNullable<Project['metadata']['permissions']>;
export type ResourceSubType = keyof Permissions;
export type PermissionLevel = Permissions[ResourceSubType];

const permissionRank = {
  none: 0,
  read: 1,
  write: 2,
  delete: 2,
} as const;

export const hasPermission = (
  permissions: Permissions | null | undefined,
  resourceSubType: ResourceSubType,
  level: PermissionLevel
) => {
  if (!permissions) return false;

  return permissionRank[permissions[resourceSubType]] >= permissionRank[level];
};

export const canRead = (
  permissions: Permissions | null | undefined,
  resourceSubType: ResourceSubType
) => hasPermission(permissions, resourceSubType, 'read');

export const canWrite = (
  permissions: Permissions | null | undefined,
  resourceSubType: ResourceSubType
) => hasPermission(permissions, resourceSubType, 'write');

export type DocumentMode = 'internal' | 'external';

const documentModeResourceMap = {
  internal: 'documentsInternal',
  external: 'documentsExternal',
} as const;

export const getDocumentPermissionResource = (mode: DocumentMode) => documentModeResourceMap[mode];

// Helper for determining whether the Documents tab should be visible.
export const canReadAnyDocuments = (permissions: Permissions | null | undefined) =>
  canRead(permissions, 'documentsInternal') || canRead(permissions, 'documentsExternal');

// Checks whether the user has read access for the specified document mode (internal or external).
export const canReadDocumentMode = (
  permissions: Permissions | null | undefined,
  mode: DocumentMode
) => canRead(permissions, getDocumentPermissionResource(mode));

// Checks whether the user has write access for the specified document mode (internal or external).
export const canWriteDocumentMode = (
  permissions: Permissions | null | undefined,
  mode: DocumentMode
) => canWrite(permissions, getDocumentPermissionResource(mode));
